import { z } from 'zod';
import { CONTRACTABLE_MODULES } from './modules';

export const BILLING_INTERVALS = ['MONTHLY', 'YEARLY'] as const;
export type BillingIntervalKey = (typeof BILLING_INTERVALS)[number];

export const PAYMENT_METHODS = ['PIX', 'CREDIT_CARD', 'BOLETO'] as const;
export type PaymentMethodKey = (typeof PAYMENT_METHODS)[number];

/** Dias de teste grátis, sem cartão. */
export const TRIAL_DAYS = 14;
/** Dias de atraso até o tenant ficar somente leitura. */
export const PAST_DUE_GRACE_DAYS = 7;
/** Dias que os dados ficam guardados depois do cancelamento. */
export const CANCELED_RETENTION_DAYS = 90;

export const quoteRequestSchema = z.object({
  modules: z.array(z.enum(CONTRACTABLE_MODULES)).max(CONTRACTABLE_MODULES.length).default([]),
  interval: z.enum(BILLING_INTERVALS).default('MONTHLY'),
  bundleKey: z.string().trim().max(40).nullable().optional(),
});
export type QuoteRequest = z.infer<typeof quoteRequestSchema>;

export interface QuoteLine {
  moduleKey: string;
  name: string;
  priceVersionId: string;
  amountCents: number;
  discountCents: number;
  totalCents: number;
}

export interface QuoteDependency {
  moduleKey: string;
  requiredBy: string;
  reason: string;
}

export interface Quote {
  interval: BillingIntervalKey;
  modules: string[];
  lines: QuoteLine[];
  addedDependencies: QuoteDependency[];
  bundle: { key: string; name: string; discountPercent: number } | null;
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  includedQuota: Record<string, number>;
  limits: Record<string, number>;
}

export const subscribeSchema = z
  .object({
    method: z.enum(PAYMENT_METHODS),
    /** Token do cartão gerado no front pelo SDK do gateway. */
    cardToken: z.string().min(3).max(200).optional(),
    interval: z.enum(BILLING_INTERVALS).optional(),
  })
  .refine((v) => v.method !== 'CREDIT_CARD' || !!v.cardToken, { message: 'Informe o cartão.', path: ['cardToken'] });

export const changeIntervalSchema = z.object({ interval: z.enum(BILLING_INTERVALS) });

export const addModuleSchema = z
  .object({
    method: z.enum(PAYMENT_METHODS).default('PIX'),
    cardToken: z.string().min(3).max(200).optional(),
  })
  .refine((v) => v.method !== 'CREDIT_CARD' || !!v.cardToken, { message: 'Informe o cartão.', path: ['cardToken'] });

export const purchaseCreditsSchema = z
  .object({
    amountCents: z.coerce.number().int().positive().max(10_000_000),
    method: z.enum(['PIX', 'CREDIT_CARD'] as const).default('PIX'),
    cardToken: z.string().min(3).max(200).optional(),
  })
  .refine((v) => v.method !== 'CREDIT_CARD' || !!v.cardToken, { message: 'Informe o cartão.', path: ['cardToken'] });

/** Métricas de uso medido e os nomes exibidos em "Meu consumo". */
export const USAGE_METRICS = {
  fiscal_documents: 'Notas fiscais emitidas',
  whatsapp_messages: 'Conversas de WhatsApp iniciadas pela empresa',
} as const;
export type UsageMetric = keyof typeof USAGE_METRICS;

/** Período de uso no formato AAAA-MM, no fuso de São Paulo. */
export function usagePeriodKey(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit' }).formatToParts(date);
  const y = parts.find((p) => p.type === 'year')!.value;
  const m = parts.find((p) => p.type === 'month')!.value;
  return `${y}-${m}`;
}
